import * as API from '@/API';
import type LockModel from '@/Models/Lock/LockModel';
import type FilingCabinetStore from '@/Stores/FilingCabinetStore';
import type { ColumnSort, PaginationState } from '@tanstack/react-table';
import { t } from 'i18next';
import { action, computed, makeObservable, observable } from 'mobx';

interface StateData {
    locks: LockModel[];
    pagination: PaginationState;
    sorting: ColumnSort[];
    globalFilter: string;
}

export default class CurrentLocksStore {
    state: StateData = {
        globalFilter: '',
        locks: [],
        pagination: { pageIndex: 0, pageSize: 25 },
        sorting: [],
    };

    loading = true;

    private readonly fcStore: FilingCabinetStore<StateData>;

    constructor(fcStore: FilingCabinetStore<StateData>) {
        this.fcStore = fcStore;

        makeObservable(this, {
            state: observable,
            loading: observable,
            filteredLocks: computed,
            set: action,
            setLocks: action,
            setState: action,
            setPagination: action,
            setSorting: action,
            reload: action,
            setLoading: action,
        });
        this.fcStore.initializeState(this.initOld, this.initNew);
        this.fcStore.updateTitle(t('currentLocks'));
    }

    private readonly initOld = (state: StateData): void => {
        this.setState({
            locks: state.locks,
            sorting: state.sorting,
            pagination: state.pagination,
            globalFilter: state.globalFilter,
        });
        void API.locks.getCurrentLocks()
            .then((res) => {
                this.setLocks(res);
                this.setLoading(false);
            })
            .catch((err) => console.error('Could not reload CurrentLocks', err));
    };

    private readonly initNew = (): void => {
        this.setLoading(true);
        void API.locks.getCurrentLocks()
            .then((res) => {
                this.setState({
                    locks: res,
                    globalFilter: '',
                    pagination: { pageIndex: 0, pageSize: 25 },
                    sorting: [],
                });
                this.setLoading(false);
            });
    };

    reload = (): void => {
        this.setLocks([]);
        this.setLoading(true);
        void API.locks.getCurrentLocks()
            .then((res) => {
                this.setLocks(res);
                this.setLoading(false);
            })
            .catch((err) => console.error('Could not reload CurrentLocks', err));
    };

    setState = (newState: StateData): void => {
        this.state = newState;
        this.fcStore.updateState(this.state);
    };

    set = <K extends keyof StateData>(prop: K, val: any): void => {
        this.state[prop] = val;
        this.fcStore.updateState(this.state);
    };

    setLocks = (val: LockModel[]): void => {
        this.state.locks = val;
        this.fcStore.updateState(this.state);
    };

    setPagination = (val: PaginationState): void => {
        this.state.pagination = val;
        this.fcStore.updateState(this.state);
    };

    setSorting = (val: ColumnSort[]): void => {
        this.state.sorting = val;
        this.fcStore.updateState(this.state);
    };

    setLoading = (val: boolean): void => {
        this.loading = val;
    };

    get filteredLocks(): LockModel[] {
        if (!this.state.globalFilter) {
            return this.state.locks;
        }

        const searchTerm = this.state.globalFilter.toLowerCase();
        return this.state.locks.filter((row: LockModel) => {
            const matchRows = ['PatientName', 'LockedBy', 'HostName'];
            let matches = false;
            if (searchTerm !== '') {
                matchRows.forEach((r: string) => {
                    if ((row[r as keyof typeof row] as string).toLowerCase().includes(searchTerm)) {
                        matches = true;
                    }
                });
            }
            else {
                matches = true;
            }
            return matches;
        });
    }
};
